import { useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Mail, MessageCircle, Calendar } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function CTASection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Headline animation
      gsap.fromTo(
        headlineRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: headlineRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Buttons animation
      gsap.fromTo(
        buttonsRef.current,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          delay: 0.1,
          scrollTrigger: {
            trigger: buttonsRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Contact animation
      gsap.fromTo(
        contactRef.current,
        { y: 16, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          delay: 0.2,
          scrollTrigger: {
            trigger: contactRef.current,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy-light relative z-[140]"
    >
      <div className="w-full px-6 lg:px-12 max-w-4xl mx-auto text-center">
        {/* Headline */}
        <div ref={headlineRef} className="mb-10 opacity-0">
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white mb-4">
            Hire leadership. Or bring yours to the market.
          </h2>
          <p className="text-body-lg text-slate">
            Start in minutes. Scale with confidence.
          </p>
        </div>

        {/* Buttons */}
        <div
          ref={buttonsRef}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 opacity-0"
        >
          <button 
            onClick={() => navigate('/register')}
            className="btn-primary flex items-center gap-2 px-8 py-4 text-base"
          >
            Find a CXO
            <ArrowRight className="w-5 h-5" />
          </button>
          <button 
            onClick={() => navigate('/register')}
            className="btn-secondary flex items-center gap-2 px-8 py-4 text-base"
          >
            Join as a CXO
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Contact Row */}
        <div
          ref={contactRef}
          className="flex flex-wrap items-center justify-center gap-6 mb-16 opacity-0"
        >
          <a
            href="mailto:sales@cxood.com"
            className="flex items-center gap-2 text-slate hover:text-teal transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span className="text-sm">sales@cxood.com</span>
          </a>
          <a
            href="mailto:support@cxood.com"
            className="flex items-center gap-2 text-slate hover:text-teal transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm">Support</span>
          </a>
          <button
            onClick={() => navigate('/register')}
            className="flex items-center gap-2 text-slate hover:text-teal transition-colors"
          >
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Book a demo</span>
          </button>
        </div>

        {/* Regions */}
        <p className="text-sm text-slate mb-16">
          Singapore • Hong Kong • Australia • India
        </p>

        {/* Footer */}
        <footer className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <span className="font-heading text-xl font-bold text-white">
                CxO <span className="text-teal">On Demand</span>
              </span>
            </div>
            <p className="text-sm text-slate">
              © {new Date().getFullYear()} CxO On Demand. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate('/privacy')}
                className="text-sm text-slate hover:text-white transition-colors"
              >
                Privacy
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="text-sm text-slate hover:text-white transition-colors"
              >
                Terms
              </button>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
}
